import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {history, shallowEqual, useDispatch, useIntl, useSelector} from 'umi';
import {isEmpty} from 'lodash-es';
import {Button, Layout, notification} from 'tea-component';
// @ts-ignore
import {utils} from 'react-grid-layout';
import {useMemoizedFn, useUpdateEffect} from "ahooks";
import classNames from "classnames";
import {buriedPoint} from '@home/utils/buriedPoint';
import {DATALAB_EDITOR, SYSTEM_NAME} from '@dbl/core/global';
import {LANGUAGE_EN} from "@home/_config/config";
import { getContactInfo } from '@dbl/services/global';
import {getShowContactDefault, safeParseBoolean} from '@/pages/datalab/pages/WorkBench/CreateReport/utils';
import {
  AI_ANALYSIS_TYPE,
  ChangeTabLayoutsType,
  gridLayoutConfig,
  layoutItemOption,
  type ILayoutItemFilter,
  type ILayoutItemText,
} from '@dbl/core/dashboard';
import {useDashboardReportStore} from "@/pages/datalab/components/DashboardLayout/hooks";
import {useEditDashboardReportStore, useSaveModal} from './hooks';
import {useTokenFailureSetSessionCache} from "@/pages/datalab/pages/Multidimensional/Analysis";
import DashboardTitle from '../components/DashboardTitle';
import DashboardLayout from '../../../components/DashboardLayout/index';
import TabsEdit from "@/pages/datalab/components/TabsEdit";
import RightModal from '../components/RightModal';
import SaveReportModal from '../components/SaveReportModal';
import AddChartNoData from '../components/AddChartNoData';
import {CustomPrompt} from '@/pages/datalab/components/CustomPrompt';
import { ChartType } from '@dbl/core/chart';
import './index.scss';

// import { useTokenFailureSetSessionCache } from '../../Multidimensional/Analysis';

const { Content } = Layout;


function CreateReport(props: any) {
  const dispatch = useDispatch();
  const { dashboardId = '', dashboardName = 'Unnamed Dashboard',  isTemplateReport: isTemplateReportUrl,  isBack: isPathBack, redirect, helpType, tab, tabId} = props?.location?.query?.dashboardMsg ? JSON.parse(props?.location?.query?.dashboardMsg) : props?.location?.query;
  const isEdit = !!dashboardId;
  const isTemplateReport = safeParseBoolean(isTemplateReportUrl);
  const isBack = safeParseBoolean(isPathBack);
  
  // 全局上下文Key
  // @ts-ignore
  const { gameCode, allPrivileges } = useSelector(
    (state: any) => ({
      gameCode: state?.datalab?.gameCode,
      userInfo: state?.home?.userInfo,
      allPrivileges: state.privilegesConfig.allPrivileges,
    }),
    shallowEqual
  );
  
  const hasChartPrivilege = allPrivileges?.includes(DATALAB_EDITOR.Chart);
  
  const {formatMessage, locale} = useIntl();
  const layoutContainerRef = useRef<null | HTMLDivElement>(null);
  const [leftLayoutWidth, setLeftLayoutWidth] = useState(0); // 左侧布局区域容器宽度
  const [showGridLine, setShowGridLine] = useState<boolean>(false); // layout 是否显示辅助线
  const [visible, setVisible] = useState(false); // 保存弹框
  const [title, setTitle] = useState<string>(dashboardName); // 报表标题
  // 离开提示弹窗是否隐藏 true为隐藏
  const [hideCustomPrompt, setHideCustomPrompt] = useState<boolean>(false);
  const takeEffect = window.sessionStorage.getItem('isUserFailure');
  // 报表显示的数据状态和逻辑
  const {
    tabLayouts,
    setTabLayouts,
    headerTabs,
    currentTabId,
    setCurrentTabId,
    filters,
    userFilter,
    layouts,
    enableCollapseFilter,
    showContact,
    setShowContact,
    contactInfo,
    setContactInfo,
    dashboardInfoRunAsync,
    dashboardInfoLoading,
    dashboardInfoError,
    originalTabLayoutsJson,
    chartsInfoLoading,
    setChartsInfoLoading,
    loadedChartsTabIds,
    userFilterScopeCharts,
  } = useDashboardReportStore({system: SYSTEM_NAME, defaultTabId: tabId});
  const hasUserFilter = userFilter?.controls?.length > 0;
  
  /** 报表编辑的状态和逻辑 */
  const {
    rightModalDragItem, // 右侧拖拽 筛选器，文本，图表， tab时的对应数据
    // 整理到 changeTabLayoutsReduce 方法中最后置空；
    // 在右侧拖拽的时候 设置对应的数据
    setRightModalDragItem,
    
    leftLayoutSelectItem, // 左侧被选中的筛选器、文本等，用于右侧显示筛选器或文本设置
    // 切换Tab时置空；
    // 在筛选器设置保存或取消；文本设置保存或取消时，置空
    // 在点击左侧卡片时onDragStart方法中设置对应的卡片数据
    // 在点击左侧筛选器时，设置对应的筛选器数据
    setLeftLayoutSelectItem,
    
    changeTabLayoutsReduce,
    rightModalRef,
  } = useEditDashboardReportStore({tabLayouts, setTabLayouts, currentTabId, setCurrentTabId, loadedChartsTabIds});
  
  
  /** 登录态失效后，重新登录恢复状态功能 */
  // useMemo 避免存储报表状态造成组件一直刷新
  const paramsObj = useMemo(() => ({
    showGridLine,
    visible,
    title,
    hideCustomPrompt,
    
    tabLayouts,
    currentTabId,
    originalTabLayoutsJson: originalTabLayoutsJson.current,
    loadedChartsTabIds: loadedChartsTabIds.current,
    rightModalDragItem,
    leftLayoutSelectItem
    
  }), [
    showGridLine,
    visible,
    title,
    hideCustomPrompt,
    
    tabLayouts,
    currentTabId,
    JSON.stringify(originalTabLayoutsJson.current),
    JSON.stringify(loadedChartsTabIds.current),
    
    rightModalDragItem,
    leftLayoutSelectItem
  
  ]);
  
  // 进行状态保存
  const {userFailureValue} = useTokenFailureSetSessionCache(paramsObj);
  useEffect(() => {
    // 本地 session存储的 报表状态 （如果是登录失效后重新进入报表编辑页面，需要用这个状态恢复之前的页面）
    // 是否需要 恢复登录失效之前报表的状态
    const isNeedRestoreState = !isEmpty(userFailureValue) && takeEffect;
    if (isNeedRestoreState) {
      setShowGridLine(userFailureValue.showGridLine);
      setVisible(userFailureValue.visible);
      setTitle(userFailureValue.title);
      setHideCustomPrompt(userFailureValue.hideCustomPrompt);
      
      setTabLayouts(userFailureValue.tabLayouts);
      setCurrentTabId(userFailureValue.currentTabId);
      originalTabLayoutsJson.current = userFailureValue.originalTabLayoutsJson;
      loadedChartsTabIds.current = userFailureValue.loadedChartsTabIds;
      
      setRightModalDragItem(userFailureValue.rightModalDragItem);
      setLeftLayoutSelectItem(userFailureValue.leftLayoutSelectItem);
      
      setShowContact(userFailureValue.showContact);
    }
    else {
      // 发起报表信息请求
      dashboardId && dashboardInfoRunAsync({game_code: gameCode, id: dashboardId, system: SYSTEM_NAME}).then(data => {
        // 根据后端返回信息 决定是否显示联系人
        setShowContact(data.show_contact);
      });
    }
    
  }, []);
  
  // 当创建报表场景下, showContact无值时, showContact的默认值
  const showContactValue = showContact ?? getShowContactDefault(gameCode);
  
  // 保存报表
  const { onSubmit, errorMessageRef, validDashboardName } = useSaveModal({
    tabLayouts,
    gameCode,
    isEdit,
    dashboardId,
    formatMessage,
    title,
    isTemplateReport,
    showContact: showContactValue,
    tab,
    tabId,
  });
  const isEn = locale === LANGUAGE_EN;
  // Content.Header组件ref
  const headerRef = useRef(null);
  // Content.Header内部拖拽方法注册
  useLayoutEffect(() => {
    
    // 判断当前拖拽的是什么?
    // 还需要判断是否已经添加了 头部Tab
    if (headerRef.current && headerTabs.length === 0) {
      function dragenter (event)  {
        if (rightModalDragItem?.type === 'tab') {
          if (event.clientY < 115) {
            headerRef.current.style.backgroundColor = '#F4F8FF';
            return;
          }
        }
        headerRef.current.style.backgroundColor = '#fff';
      }
      
      function dragleave(event) {
        if (event.clientY >= 115)
          headerRef.current.style.backgroundColor = '#fff';
      }
      
      function drop (event) {
        event.preventDefault();
        if (rightModalDragItem?.type === 'tab') {
          changeTabLayoutsReduce(ChangeTabLayoutsType.ADD_TAB, {isEn});
          headerRef.current.style.backgroundColor = '#fff';
        }
      }
      
      function dragover (event) {
        event.preventDefault();
      }
      
      headerRef?.current?.addEventListener('dragover', dragover);
      headerRef?.current?.addEventListener('dragenter', dragenter);
      headerRef?.current?.addEventListener('dragleave', dragleave);
      headerRef?.current?.addEventListener('drop', drop);
      
      return () => {
        headerRef?.current?.removeEventListener('dragenter', dragenter);
        headerRef?.current?.removeEventListener('dragleave', dragleave);
        headerRef?.current?.removeEventListener('drop', drop);
        headerRef?.current?.removeEventListener('dragover', dragover);
      };
    }
  }, [rightModalDragItem]); // 如果依赖 rightModalDragItem, 就会造成经常创建和销毁事件
  
  
  // 监听resize事件
  useEffect(() => {
    // 监听宽度大小,设置节流
    const resizeChange = () => {
      if (layoutContainerRef.current) {
        setLeftLayoutWidth(layoutContainerRef.current?.offsetWidth);
      }
    };
    resizeChange();
    window.addEventListener('resize', resizeChange);
    return () => window.removeEventListener('resize', resizeChange);
  }, []);
  
  useEffect(() => {
    
    if (isTemplateReport) {
      // 如果是模板报表
      buriedPoint({
        pageId: '601602',
        pageName: dashboardName,
        extInfo: `{"pageName": "${dashboardName}", "pageNameCn": "", "id": "${dashboardId}"}`,
        pagePath: '/WorkBench/MyDashboard/CreateDashboard',
        uid: gameCode,
        typeId: 'datalab_pc_v2',
        menuId: '0',
      });
      
      return;
    }
    
    buriedPoint({
      pageId: isEdit ? '601202' : '601201',
      pageName: isEdit ? dashboardName : '新建报表',
      extInfo: isEdit ? `{"pageName": "${dashboardName}", "pageNameCn": "", "id": "${dashboardId}"}` : `{"pageName": "New Dashboard", "pageNameCn": "新建报表", "id": ""}`,
      pagePath: '/WorkBench/MyDashboard/CreateDashboard',
      uid: gameCode,
      typeId: 'datalab_pc_v2',
      menuId: '0',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode]);
  
  useEffect(() => {
    // 展开左侧目录
    dispatch({
      type: 'datalab/save',
      payload: {
        isExpendLeftMenu: false
      }
    });
    
    return () => {
      // 展开左侧目录
      dispatch({
        type: 'datalab/save',
        payload: {
          isExpendLeftMenu: true
        }
      });
      
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // 左侧筛选器和文本卡片被选中
  const changeLeftLayoutSelectItem = (item: ILayoutItemFilter | ILayoutItemText | null, isOpenRight?: boolean) => {
    setLeftLayoutSelectItem(item);
    isOpenRight && rightModalRef.current?.openRightModal?.();
  };
  
  // 是否需要保存 true是需要保存
  const isNeedSave = (title !== dashboardName) || (originalTabLayoutsJson.current !== JSON.stringify(tabLayouts));
  
  const saveDashboard = useMemoizedFn((path: string) => {
    setHideCustomPrompt(true);
    onSubmit(path);
    // if (isNeedSave) {
    //
    // } else {
    //   history.push(`${path}&dashboardId=${dashboardId}&dashboardName=${title}&isTemplateReport=${isTemplateReport}`);
    // }
    
  });
  
  const showOrHideSaveModel = useMemoizedFn((val: boolean) => {
    setVisible(val);
    setHideCustomPrompt(val);
  });
  
  useUpdateEffect(() => {
    
    // 在开启show contact时, 但是没有联系人信息, 则发起请求
    // 场景
    //  * 从 关闭 到 开启
    //  * 开启 到 关闭
    if (isEdit && (!dashboardInfoLoading && isEmpty(contactInfo) && showContactValue)) {
      getContactInfo().then((data) => {
        setContactInfo(data?.contact || {});
      }).catch(e => {
        console.error("获取联系人信息失败", e);
        notification.error({description: formatMessage({id: 'dbl_create_dashboard_contact_error'})});
      });
    }
  }, [contactInfo, showContactValue]);
  
  useEffect(() => {
    //  * 新建场景, 默认开启, 默认发起请求 !isEdit 是新建
    if (!isEdit) {
      getContactInfo().then((data) => {
        setContactInfo(data?.contact || {});
      }).catch(e => {
        console.error("获取联系人信息失败", e);
        notification.error({description: formatMessage({id: 'dbl_create_dashboard_contact_error'})});
      });
    }
  }, []);
  const isCreate = isEmpty(filters) && isEmpty(userFilter.controls) && isEmpty(layouts) && !dashboardInfoLoading;
  
  return (
    <Content className='dbl-dashboard'>
      <Content.Header
        showBackButton
        onBackButtonClick={() => {
          // https://tapd.woa.com/tapd_fe/20427644/story/detail/1020427644118648771?from_iteration_id=1020427644001835513
          // 报表预览页-编辑-未修改-点击返回时-返回报表预览页
          // 暂不考虑其他口径进入，只有指定的操作路径进入，会命中
          if(redirect){
            history.push(`/datalab/${gameCode}/${redirect}`);
            return;
          }
          // TODO 编辑和创建页面 isEdit？？？
          // (!isNeedSave && isEdit) || isBack ? history.goBack() :  history.push(`/datalab/${gameCode}/WorkBench/MyDashboard`);
          !isNeedSave|| isBack ? history.goBack() :  history.push(`/datalab/${gameCode}/WorkBench/MyDashboard`);
        }}
        className={classNames('dbl-dashboard-header', {'dbl-dashboard-header-with-tabs': headerTabs.length > 0})}
        title={<DashboardTitle title={title} errorMessageRef={errorMessageRef} setTitle={setTitle} validDashboardName={validDashboardName}/>}
        operation={
          <Button
            type='primary'
            disabled={dashboardInfoLoading}
            onClick={() => showOrHideSaveModel(true)}>
            {formatMessage({id: 'dbl_common_save'})}
          </Button>
        }
        ref={headerRef}
      >
        {
          headerTabs?.length > 0 ? (<TabsEdit
            className={'dbl-dashboard-edit-tabs'}
            tabs={headerTabs}
            activeId={currentTabId}
            onActive={(tab: { id: string; label: string; }) => {
              if (tab.id === currentTabId) return;
              setCurrentTabId(tab.id);
              // fix:解决图表重复请求的问题
              // 切换 tab 时设置 loading 状态，防止在图表数据加载完成前重复请求
              // 原因：tab 切换会触发图表重新加载，需要显示 loading 状态
              setChartsInfoLoading(true);
              setLeftLayoutSelectItem(null);
              history.replace({
                pathname: history?.location?.pathname,
                query: {
                  ...(history?.location?.query || {}),
                  tab: typeof tab?.label === 'string' ? tab?.label : tab?.id,
                  tabId: tab?.id,
                }
              });
            }}
            changeTabLayoutsReduce={changeTabLayoutsReduce}
            onDeleteTab={(tab) => { changeTabLayoutsReduce(ChangeTabLayoutsType.DELETE_TAB, {tabId: tab.id})}}
            onRenameTab={(tab) => { changeTabLayoutsReduce(ChangeTabLayoutsType.EDIT_TAB_NAME, {tabId: tab.id, tabName: tab.label, tabNameCN: tab.label})}}
            onAddTab={() => { changeTabLayoutsReduce(ChangeTabLayoutsType.ADD_TAB)}}
            onSort={(newTabs) => { changeTabLayoutsReduce(ChangeTabLayoutsType.CHANGE_TAB_ORDER, {tabs: newTabs}) }}
          />) : ''
        }
      </Content.Header>
      
      {/* 左右布局，左侧编辑区，右侧工具区 */}
      <Content.Body full className='dbl-dashboard-content'>
        {/* 编辑区主体 */}
        <div className={classNames('dbl-dashboard-body', {'dbl-dashboard-body-with-tab': headerTabs.length > 0})}>
          {/* 左侧编辑区,视图layout区域*/}
          <div ref={layoutContainerRef} className='dbl-dashboard-body-left'>
            {/* layoutAll?.length != 0 */}
            {isCreate && (!rightModalDragItem) ?  (
              // TODO 新建页面 显示联系人
              <AddChartNoData
                gameCode={gameCode}
                layoutLoading={false}
                handleOnSubmit={saveDashboard}
                // 是否有新建图表的权限
                hasChartPrivilege={hasChartPrivilege}
                showContact={showContactValue}
                contactInfo={contactInfo}
              />
            ) : (
              <DashboardLayout
                // key值的设置可以保证在切换页签的时候， 图表请求不会发起两次
                key={currentTabId}
                className={'dbl-edit-dashboard'}
                gameCode={gameCode}
                dashboardId={dashboardId}
                tabLayouts={tabLayouts}
                filters={filters}
                userFilter={userFilter}
                enableCollapseFilter={enableCollapseFilter}
                layouts={layouts}
                loading={dashboardInfoLoading}
                error={dashboardInfoError}
                isNoData={!rightModalDragItem}
                containerWidth={leftLayoutWidth}
                // 报表内渲染的图表信息请求加载态
                chartsInfoLoading={isCreate ? false : chartsInfoLoading}
                // true 报表编辑，false报表展示
                isEdit
                isDroppable
                // TODO 以下是报表编辑所需要的
                changeTabLayoutsReduce={changeTabLayoutsReduce}
                // 报表编辑时 会执行到的事件
                onDragStart={() => { // 刚开始拖动
                  // layout, oldItem, newItem
                  setShowGridLine(true);
                }}
                onDragStop={(layout) => { //
                  setShowGridLine(false);
                  changeTabLayoutsReduce(ChangeTabLayoutsType.UPDATE_CARD_LAYOUT, {newLayouts: layout});
                }}
                onResize={() => {
                  setShowGridLine(true);
                }}
                onResizeStop={(layout) => {
                  setShowGridLine(false);
                  changeTabLayoutsReduce(ChangeTabLayoutsType.UPDATE_CARD_LAYOUT, {newLayouts: layout});
                  
                }}
                onDropDragOver={() => {
                  // 在布局中一直不松开鼠标, 就会一直执行
                  if (!rightModalDragItem) {
                    return false;
                  }
                  // 显示拖拽过程中 占位卡片大小
                  const option = layoutItemOption?.[rightModalDragItem?.chartType];
                  return option || layoutItemOption.default;
                }}
                handleFilterDrop={()=> {
                  // 拖拽到布局区域, 松开鼠标后执行
                  if (rightModalDragItem?.type === 'filter' && !filters?.length) {
                    // 添加筛选器
                    changeTabLayoutsReduce(ChangeTabLayoutsType.ADD_FILTER);
                  } else if (rightModalDragItem?.type === 'userFilter' && !hasUserFilter) {
                    // 添加用户筛选器
                    changeTabLayoutsReduce(ChangeTabLayoutsType.INIT_USER_FILTER, {scopeCharts: userFilterScopeCharts });
                  }
                }}
                onDrop={(layout, layoutItem) => {
                  // 拖拽到布局区域, 松开鼠标后执行
                  if (rightModalDragItem?.type === 'filter') {
                    // 添加筛选器
                    changeTabLayoutsReduce(ChangeTabLayoutsType.ADD_FILTER);
                  } else if (rightModalDragItem?.type === 'userFilter' && !hasUserFilter) {
                    // 添加用户筛选器
                    changeTabLayoutsReduce(ChangeTabLayoutsType.INIT_USER_FILTER, {scopeCharts: userFilterScopeCharts });
                  } else if (rightModalDragItem?.type === 'text' || rightModalDragItem?.task_id || rightModalDragItem?.type === AI_ANALYSIS_TYPE) { // 有task_id说明是图表数据
                    // 添加 文本和图表
                    const viewType = rightModalDragItem.chartType;
                    const id = `${viewType}-${Math.random()}`;
                    
                    // TODO 存储的数据需要优化
                    // const newLayoutItem = {...rightModalDragItem, i: id, id};
                    
                    const { cols, compactType, allowOverlap } = gridLayoutConfig;
                    const chartLayouts = utils.compact(
                      [
                        ...layout.filter(item => item.i !== '__dropping-elem__'), // layout 是外层布局组件的布局数组
                        {
                          ...layoutItem,
                          i: id,
                        }
                      ],
                      compactType,
                      cols,
                      allowOverlap
                    );
                    
                    // 需要从 filter中将表格类型的 明细模式还是聚合模式的信息解析出来, 存储在新更新的图表卡片中, 设置user filter的范围时需要用到
                    let mode;
                    if (rightModalDragItem?.chartType === ChartType.Table) {
                      mode = JSON.parse(rightModalDragItem?.filter || '{}')?.query_mode;
                    }
                    const allChartTaskIds = tabLayouts
                      ?.flatMap((tabItem) => tabItem?.layouts || [])
                      ?.map((layoutItem) => String(layoutItem?.task_id || ''))
                      ?.filter(Boolean) || [];
                    changeTabLayoutsReduce(ChangeTabLayoutsType.UPDATE_CARD_LAYOUT, {
                      newLayouts: chartLayouts,
                      cardInfo: {
                        ...rightModalDragItem,
                        ...(rightModalDragItem?.type === AI_ANALYSIS_TYPE ? {
                          promptInfo: {
                            analysisScope: {
                              type: 'all',
                              taskIds: allChartTaskIds,
                            },
                            promptId: 1,
                            isCustom: false,
                          },
                        } : {}),
                        mode,
                        i: id,
                        id,
                        name: rightModalDragItem?.name || rightModalDragItem?.title,
                      }
                    });
                  }
                  
                }}
                onLayoutChange={() => { // 布局区域的任何变更结束, 都会执行这个事件
                  // newLayout
                  // const newCard = newLayout.find(item => item.i === currentDragIdRef.current);
                  // newCard && changeTabLayoutsReduce?.(ChangeTabLayoutsType.CHANGE_CARD, {cardInfo: {
                  //   h: newCard.h,
                  //   w: newCard.w,
                  //   x: newCard.x,
                  //   y: newCard.y
                  // }});
                  // 切换页签也会执行这个方法，造成重复渲染
                  
                  setShowGridLine(false);
                  
                  // 拖拽卡片改变位置, 改变卡片的宽高, 最终都会执行这个方法 (拖拽最终结果和拖拽之前相同, 则不会执行)
                  // 但在拖拽添加卡片的过程中, 也会执行这个方法,
                  // if (!rightModalDragItem) { // 所以当 rightModalDragItem 为 null的时候, 说明不是拖拽添加卡片
                  //   changeTabLayoutsReduce(ChangeTabLayoutsType.UPDATE_CARD_LAYOUT, {newLayouts: newLayout});
                  // }
                }}
                
                // 是否展示背景的栅格线
                showGridLine={showGridLine || rightModalDragItem}
                changeLeftLayoutSelectItem={changeLeftLayoutSelectItem}
                // 卡片点击编辑，跳转之前保存报表
                handleOnSubmit={saveDashboard}
                showContact={showContactValue}
                contactInfo={contactInfo}
              />
            ) }
          </div>
          
          {/* 右侧工具区*/}
          <RightModal
            ref={rightModalRef}
            gameCode={gameCode}
            filters={filters}
            userFilter={userFilter}
            enableCollapseFilter={enableCollapseFilter}
            views={layouts}
            tabLayouts={tabLayouts}
            changeTabLayoutsReduce={changeTabLayoutsReduce}
            
            changeLeftLayoutSelectItem={changeLeftLayoutSelectItem}
            leftLayoutSelectItem={leftLayoutSelectItem}
            changeRightModalDragItem={(item: any) => {
              // 右侧向布局区域拖动 筛选器 或者 文本的对象
              setRightModalDragItem(item);
            }}
            // 是否有新建图表的权限
            hasChartPrivilege={hasChartPrivilege}
            // 报表内容有修改才可以进行保存
            handleOnSubmit={saveDashboard}
            subType={helpType}
            // 设置是否显示联系人
            changeShowContact={setShowContact}
            showContact={showContactValue}
            hasUserFilter={hasUserFilter}
          />
        </div>
        {
          // 查验代码发现 由于SaveReportModal组件内部逻辑原因，每次打开弹窗都需要重新创建SaveReportModal组件
          visible && (
            <SaveReportModal
              gameCode={gameCode}
              tab={tab}
              tabId={tabId}
              // isEdit={isEdit}
              dashboardId={dashboardId}
              title={title}
              tabLayouts={tabLayouts}
              closeModal={() => showOrHideSaveModel(false)}
              isTemplateReport={isTemplateReport}
              showContact={showContactValue}
            />
          )
        }
        
        <CustomPrompt
          title={formatMessage({id: 'dbl_create_dashboard_leave_title'})}
          message={formatMessage({id: 'dbl_create_dashboard_leave_des'})}
          onConfirm={() => {
            // 点击保存按钮时，要保存报表
            onSubmit();
            
            // TODO 保存成功之后的跳转？？？
          }}
          confirmBtn={formatMessage({id: 'dbl_common_save'})}
          cancelBtn={formatMessage({id: 'dbl_common_leave'})}
          onCancle={(pathname: string) => {
            // 点击离开按钮
            // 进入报表编辑页面的入口：
            // 1. 报表中心直接进入
            // 2. 报表预览页面
            // 3. 链接路径直接进入
            // 无论哪个
            history.push(pathname);
            // TODO 编辑和创建页面
            // !hashEqualForSave && isEdit ? history.goBack() : history.push(pathname);
          }}
          isPrompt={isNeedSave && !hideCustomPrompt}
          ignore={(location) => location?.query?.tab}
        />
      </Content.Body>
    </Content>
  );
}

export default CreateReport;
